import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from '@mui/material';
import { getFirestore, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import Iconify from '../components/iconify';
import firebaseApp from '../utils/firebaseConfig';
import { sortLanes, laneLabel } from '../utils/lanes';

const db = getFirestore(firebaseApp);

// Registro del Campo de Tiro — una fila por PERSONA que estuvo en el range:
// el cliente principal, la persona para quien se reservó, el acompañante y
// las personas adicionales añadidas por el staff en los detalles.
// Por default entra todo el mundo, incluidos los no-shows (pedido 2026-09-15);
// el filtro "Solo shows" deja únicamente a los que sí llegaron.
const INCLUDED_STATUSES = ['confirmed', 'completed', 'no_show'];

// "Llegó" = marcado con el botón Showed, o completada (Mark as Completed se
// usa cuando el cliente ya tiró y se fue antes). No-show nunca cuenta.
const didShow = (r) => r.status !== 'no_show' && (r.showed === true || r.status === 'completed');
const attendanceLabel = (r) => {
  if (r.status === 'no_show') return 'No show';
  return didShow(r) ? 'Showed' : '';
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
}

function esc(s) {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Aplana una reservación en filas por persona.
// opts.skipGuest: el acompañante tiene su PROPIO doc (guestOf) en el listado
// (lane aparte / curso), así que su fila sale de ese doc y no de aquí —
// antes salía doble (pedido 2026-09-15).
// opts.asCompanion: este doc ES el del acompañante → rol "Acompañante" y,
// si no trae calibres propios, los del acompañante en la reserva principal.
function reservationToRows(r, opts = {}) {
  const base = {
    date: r.date,
    time: `${formatTime12(r.startTime)}${r.endTime ? ` - ${formatTime12(r.endTime)}` : ''}`,
    category: r.categoryName || '',
    categoryId: r.categoryId || '',
    lane: r.laneName || '',
    laneId: r.laneId || '',
    code: r.confirmationCode || '',
    status: r.status,
    showed: didShow(r),
    attendance: attendanceLabel(r),
  };
  const rows = [];
  const mainCalibers = (r.calibers || []).join(', ');
  if (opts.asCompanion) {
    const fromMain = opts.mainGuest && opts.mainGuest.calibers ? opts.mainGuest.calibers.join(', ') : '';
    rows.push({
      ...base,
      name: r.userName || (opts.mainGuest && opts.mainGuest.name) || '',
      license: r.licenseNumber || (opts.mainGuest && opts.mainGuest.licenseNumber) || '',
      calibers: mainCalibers || fromMain,
      role: 'Acompañante',
    });
    return rows;
  }
  if (r.reservationFor && r.reservationFor.name) {
    // La reservación fue PARA otra persona: esa es quien tiró
    rows.push({
      ...base,
      name: r.reservationFor.name,
      license: r.reservationFor.licenseNumber || '',
      calibers: mainCalibers,
      role: 'Cliente',
    });
  } else {
    rows.push({
      ...base,
      name: r.userName || r.userEmail || '',
      license: r.licenseNumber || '',
      calibers: mainCalibers,
      role: 'Cliente',
    });
  }
  if (r.guest && r.guest.name && !opts.skipGuest) {
    rows.push({
      ...base,
      lane: r.guest.laneName || base.lane,
      laneId: r.guest.laneId || base.laneId,
      name: r.guest.name,
      license: r.guest.licenseNumber || '',
      calibers: (r.guest.calibers || []).join(', '),
      role: 'Acompañante',
    });
  }
  (r.attendees || []).forEach((a) => {
    rows.push({
      ...base,
      name: a.name,
      license: a.licenseNumber || '',
      calibers: (a.calibers || []).join(', '),
      role: 'Adicional',
    });
  });
  return rows;
}

export default function ReportsPage() {
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [onlyShowed, setOnlyShowed] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLane, setFilterLane] = useState('');
  const [categories, setCategories] = useState([]);
  const [lanes, setLanes] = useState([]);
  // Todas las filas del rango de fechas; los filtros se aplican encima sin
  // volver a consultar (y el CSV / impresión salen ya filtrados).
  const [allRows, setAllRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [catsSnap, lanesSnap] = await Promise.all([
          getDocs(collection(db, 'reservation_categories')),
          getDocs(collection(db, 'lanes')),
        ]);
        const cats = catsSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => c.type !== 'course')
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || String(a.name || '').localeCompare(String(b.name || ''), 'es'));
        setCategories(cats);
        setLanes(sortLanes(lanesSnap.docs.map((d) => ({ id: d.id, ...d.data() })), cats));
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Lanes que aplican a la categoría escogida (o todos)
  const laneChoices = useMemo(
    () => lanes.filter((l) => !filterCategory || (Array.isArray(l.categoryIds) && l.categoryIds.includes(filterCategory))),
    [lanes, filterCategory]
  );

  const rows = useMemo(() => {
    if (!allRows) return null;
    return allRows
      .filter((r) => !filterCategory || r.categoryId === filterCategory)
      .filter((r) => !filterLane || r.laneId === filterLane)
      .filter((r) => !onlyShowed || r.showed);
  }, [allRows, filterCategory, filterLane, onlyShowed]);

  const filterSummary = [
    filterCategory ? (categories.find((c) => c.id === filterCategory)?.name || '') : '',
    filterLane ? (lanes.find((l) => l.id === filterLane)?.name || '') : '',
    onlyShowed ? 'solo shows' : '',
  ].filter(Boolean).join(' · ');

  const handleGenerate = async () => {
    if (!startDate || !endDate || endDate < startDate) {
      setError('Escoge un rango de fechas válido.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const snap = await getDocs(query(
        collection(db, 'reservations'),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date'),
      ));
      const reservations = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => INCLUDED_STATUSES.includes(r.status))
        // Los bloqueos de lanes viven en la misma colección pero no son visitas
        .filter((r) => r.categoryType !== 'block' && r.categoryId !== '__BLOCK__')
        .sort((a, b) => (a.date + (a.startTime || '')).localeCompare(b.date + (b.startTime || '')));
      // Docs de acompañante (guestOf) → una sola fila por acompañante
      const byId = new Map(reservations.map((r) => [r.id, r]));
      const mainsWithCompanionDoc = new Set(reservations.filter((r) => r.guestOf).map((r) => r.guestOf));
      setAllRows(reservations.flatMap((r) => {
        if (r.guestOf) {
          const main = byId.get(r.guestOf);
          return reservationToRows(r, { asCompanion: true, mainGuest: main ? main.guest : null });
        }
        return reservationToRows(r, { skipGuest: mainsWithCompanionDoc.has(r.id) });
      }));
    } catch (e) {
      console.error(e);
      setError('Error generando el reporte. Intenta de nuevo.');
    }
    setLoading(false);
  };

  const handleExportCsv = () => {
    if (!rows || rows.length === 0) return;
    const header = ['Fecha', 'Hora', 'Nombre', 'Núm. Licencia', 'Calibres', 'Rol', 'Categoría', 'Lane', 'Asistencia', 'Código'];
    const csvRows = rows.map((r) => [r.date, r.time, r.name, r.license, r.calibers, r.role, r.category, r.lane, r.attendance, r.code]);
    const csv = [header, ...csvRows]
      .map((cols) => cols.map((c) => `"${String(c || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    // BOM para que Excel abra los acentos bien
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const suffix = [filterCategory ? 'cat' : '', filterLane ? 'lane' : '', onlyShowed ? 'shows' : ''].filter(Boolean).join('-');
    link.download = `registro-campo-tiro-${startDate}_${endDate}${suffix ? `-${suffix}` : ''}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handlePrint = () => {
    if (!rows || rows.length === 0) return;
    const bodyRows = rows.map((r) => `
      <tr>
        <td>${esc(r.date)}</td>
        <td>${esc(r.time)}</td>
        <td>${esc(r.name)}</td>
        <td>${esc(r.license)}</td>
        <td>${esc(r.calibers)}</td>
        <td>${esc(r.category)}${r.lane ? ` — ${esc(r.lane)}` : ''}</td>
        <td>${esc(r.attendance)}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8" />
      <title>Registro del Campo de Tiro</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 24px; color: #222; }
        h2 { margin: 0 0 4px; }
        .sub { color: #666; margin-bottom: 16px; font-size: 13px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #999; padding: 6px 8px; text-align: left; }
        th { background: #f0f0f0; }
        @media print { body { margin: 8px; } }
      </style></head><body>
      <h2>B &amp; B Target Center — Registro del Campo de Tiro</h2>
      <div class="sub">${esc(startDate)}${endDate !== startDate ? ` al ${esc(endDate)}` : ''}${filterSummary ? ` · ${esc(filterSummary)}` : ''} · ${rows.length} persona(s)</div>
      <table><thead><tr>
        <th>Fecha</th><th>Hora</th><th>Nombre</th><th>Núm. Licencia</th><th>Calibres</th><th>Categoría / Lane</th><th>Asistencia</th>
      </tr></thead><tbody>${bodyRows}</tbody></table>
      <script>window.onload = () => window.print();</script>
    </body></html>`;
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
    }
  };

  return (
    <>
      <Helmet>
        <title> Reportes | BBTC </title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 1 }}>
          Registro del Campo de Tiro
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Genera el registro con día de la reservación, nombre del cliente, número de licencia y calibres que usó.
          Incluye acompañantes y personas adicionales añadidas en los detalles de cada reservación.
        </Typography>

        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label="Desde"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Hasta"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Iconify icon="eva:search-fill" />}
            >
              Generar
            </Button>
            <Button
              variant="outlined"
              onClick={handleExportCsv}
              disabled={!rows || rows.length === 0}
              startIcon={<Iconify icon="eva:download-outline" />}
            >
              Exportar CSV
            </Button>
            <Button
              variant="outlined"
              onClick={handlePrint}
              disabled={!rows || rows.length === 0}
              startIcon={<Iconify icon="eva:printer-outline" />}
            >
              Imprimir
            </Button>
          </Stack>
          {/* Filtros: se aplican al listado, al CSV y a la impresión */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mt: 2 }}>
            <TextField
              select
              label="Categoría"
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setFilterLane(''); }}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Todas</MenuItem>
              {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Lane"
              value={filterLane}
              onChange={(e) => setFilterLane(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {laneChoices.map((l) => (
                <MenuItem key={l.id} value={l.id}>{filterCategory ? l.name : laneLabel(l, categories)}</MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={<Checkbox checked={onlyShowed} onChange={(e) => setOnlyShowed(e.target.checked)} size="small" />}
              label={<Typography variant="body2">Solo incluir shows</Typography>}
            />
            <Typography variant="caption" color="text.secondary">
              Por default salen todos (incluye no-shows). "Solo shows" deja a los marcados Showed o completados.
            </Typography>
          </Stack>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Card>

        {rows && (
          <Card>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
              <Typography variant="subtitle1">
                {rows.length} persona(s) en el registro{filterSummary ? ` · ${filterSummary}` : ''}
                {allRows && rows.length !== allRows.length ? ` (de ${allRows.length})` : ''}
              </Typography>
            </Stack>
            <TableContainer sx={{ maxHeight: 640 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Hora</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Núm. Licencia</TableCell>
                    <TableCell>Calibres</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Categoría / Lane</TableCell>
                    <TableCell>Asistencia</TableCell>
                    <TableCell>Código</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={`${r.code}-${r.name}-${i}`} hover>
                      <TableCell>{r.date}</TableCell>
                      <TableCell>{r.time}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.license || '—'}</TableCell>
                      <TableCell>{r.calibers || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.role}
                          size="small"
                          color={r.role === 'Cliente' ? 'default' : r.role === 'Acompañante' ? 'warning' : 'info'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{r.category}{r.lane ? ` — ${r.lane}` : ''}</TableCell>
                      <TableCell>
                        {r.attendance === 'Showed' && <Chip label="Showed" size="small" color="success" variant="outlined" />}
                        {r.attendance === 'No show' && <Chip label="No show" size="small" color="error" variant="outlined" />}
                        {!r.attendance && '—'}
                      </TableCell>
                      <TableCell>{r.code}</TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {allRows && allRows.length > 0 ? 'Ninguna fila cumple los filtros.' : 'No hay reservaciones en ese rango de fechas.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Container>
    </>
  );
}
